/**
 * Знакомство при первом входе (21-retention-plan.md §6).
 *
 * Ревью 25.09.2026: из 15 человек хотя бы один подход записали двое. Новый
 * человек открывал приложение, видел «Программы пока нет» и уходил. Здесь —
 * три вопроса и готовая программа в календаре с сегодняшнего дня.
 */

export type Where = "gym" | "home" | "bodyweight";
export type Experience = "new" | "trained";
export type Frequency = 3 | 4 | 5;

export interface Answers {
  where: Where;
  experience?: Experience;
  frequency?: Frequency;
}

/** Какие вопросы задавать: дома и без железа — опыт и частота не меняют выбор. */
export function questionsFor(answers: Partial<Answers>): ("where" | "experience" | "frequency")[] {
  if (!answers.where || answers.where !== "gym") return ["where"];
  if (answers.experience === "trained") return ["where", "experience", "frequency"];
  return ["where", "experience"];
}

/** Ответы собраны полностью — можно предлагать программу. */
export function complete(answers: Partial<Answers>): answers is Answers {
  return questionsFor(answers).every((q) => answers[q] !== undefined);
}

/**
 * Какую готовую программу предложить.
 *
 * Новичку в зале — тренажёры и гантели, а не штанга с первого дня: первая
 * тренировка должна закончиться желанием прийти ещё, а не страхом перед
 * приседом. Опытному — по числу тренировок в неделю.
 */
export function suggestTemplate(answers: Answers): string {
  if (answers.where === "home") return "home";
  if (answers.where === "bodyweight") return "bodyweight";
  if (answers.experience !== "trained") return "beginner";
  if (answers.frequency === 4) return "upper-lower";
  if (answers.frequency === 5) return "ppl";
  return "fullbody";
}

/**
 * Показывать ли знакомство.
 *
 * Только тому, у кого нет ни программы, ни тренировки: остальным оно мешает.
 * «Пропустить» помнится на устройстве — на другом покажется ещё раз, но
 * человек без программы и есть тот, кому оно нужно.
 */
export function shouldOnboard(hasProgram: boolean, hasWorkout: boolean, dismissed: boolean): boolean {
  return !hasProgram && !hasWorkout && !dismissed;
}

const KEY = "rack.onboarding";

export function dismissed(): boolean {
  try {
    return localStorage.getItem(KEY) === "skipped";
  } catch {
    return false;
  }
}

export function dismiss(): void {
  try {
    localStorage.setItem(KEY, "skipped");
  } catch {
    // Не запомнили — знакомство покажется ещё раз, это не беда.
  }
}
